import React from 'react';
import { useOfflineToggle } from './useOfflineToggle';

/**
 * Online / Offline segmented control.
 *
 * A rounded track holding two segments. Only the active segment is painted as a
 * raised chip with a status dot; the other is quiet muted text. Shows *effective*
 * connectivity: Offline when the user chose offline OR the browser lost its
 * connection. With no network both segments are disabled — you can't force
 * "online" — and it flips back automatically on reconnect.
 */
const OfflineToggle = ({ className = '', scale = 1 }) => {
  const { offline, isBrowserOnline, isManualOffline, toggle } = useOfflineToggle();

  const title = !isBrowserOnline
    ? 'No internet connection — working offline'
    : isManualOffline
      ? 'Working offline — click to go back online'
      : 'Online — click to work offline';

  const selectOnline = () => { if (offline && isBrowserOnline) toggle(); };
  const selectOffline = () => { if (!offline) toggle(); };

  const segmentBase = 'inline-flex items-center gap-1 h-[22px] px-2 rounded-full text-[10px] font-semibold leading-none transition-colors';
  const activeSegment = 'bg-surface text-gray-800 shadow-sm cursor-default';
  const inactiveSegment = `text-gray-500 ${isBrowserOnline ? 'hover:text-gray-700' : 'cursor-not-allowed'}`;

  return (
    <div
      role="group"
      aria-label="Work online or offline"
      title={title}
      style={scale !== 1 ? { transform: `scale(${scale})`, transformOrigin: 'center' } : undefined}
      className={`inline-flex items-center gap-0.5 p-[2px] rounded-full bg-[#f1f3f8] flex-shrink-0 whitespace-nowrap ${isBrowserOnline ? 'opacity-100' : 'opacity-90'} ${className}`}
    >
      <button
        type="button"
        onClick={selectOnline}
        disabled={!isBrowserOnline}
        aria-pressed={!offline}
        className={`${segmentBase} ${!offline ? activeSegment : inactiveSegment}`}
      >
        {!offline && <span className="h-[5px] w-[5px] rounded-full bg-green-500 flex-shrink-0" />}
        Online
      </button>
      <button
        type="button"
        onClick={selectOffline}
        disabled={!isBrowserOnline}
        aria-pressed={offline}
        className={`${segmentBase} ${offline ? activeSegment : inactiveSegment}`}
      >
        {offline && <span className="h-[5px] w-[5px] rounded-full bg-amber-500 flex-shrink-0" />}
        Offline
      </button>
    </div>
  );
};

export default OfflineToggle;
